import Button from '@mui/material/Button';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Table from '@mui/material/Table';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import TextField from '@mui/material/TextField';

import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import dayjs from 'dayjs';
import 'dayjs/locale/el';

// Set Greek locale for dayjs
dayjs.locale('el');
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';

dayjs.extend(weekday);
dayjs.extend(localeData);

export default function Salaries() {
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(() => {
    // Use the same logic as Schedule component
    return dayjs().startOf('isoWeek');
  });

  useEffect(() => {
    fetch('/api/employees').then(res => res.json()).then(setEmployees);
  }, []);

  useEffect(() => {
    const weekStart = dayjs(selectedWeek).format('YYYY-MM-DD');
    console.log('=== FETCHING SHIFTS ===');
    console.log('Selected week object:', selectedWeek);
    console.log('Week start string:', weekStart);
    console.log('Full URL:', `/api/shifts?week_start=${weekStart}`);
    
    fetch(`/api/shifts?week_start=${weekStart}`)
      .then(res => res.json())
      .then(data => {
        console.log('=== SHIFTS RECEIVED ===');
        console.log('Raw shifts data:', data);
        console.log('Number of shifts:', data.length);
        setShifts(data);
      })
      .catch(error => {
        console.error('Error fetching shifts:', error);
      });
  }, [selectedWeek]);

  // Υπολογισμός ημερομηνιών εβδομάδας (Δευτέρα - Κυριακή)
  function getWeekDates(date) {
    // Use the same logic as Schedule component
    const startOfWeek = dayjs(date).startOf('isoWeek');
    return Array.from({ length: 7 }, (_, i) => {
      return startOfWeek.add(i, 'day').toDate();
    });
  }
  const weekDates = getWeekDates(selectedWeek);
  const weekStart = dayjs(selectedWeek).format('YYYY-MM-DD');

  // Χρησιμοποίησε το backend API για τον υπολογισμό αμοιβών με ιστορική ακεραιότητα
  const [salariesFromAPI, setSalariesFromAPI] = useState([]);
  
  useEffect(() => {
    // Πάντα στείλε την εβδομάδα στο API
    const weekParam = `?week=${weekStart}`;
    console.log('Fetching salaries for week:', weekStart);
    fetch(`/api/salaries${weekParam}`)
      .then(res => res.json())
      .then(setSalariesFromAPI)
      .catch(err => console.error('Error fetching salaries:', err));
  }, [weekStart, employees]); // Επανατρέχει όταν αλλάζει εβδομάδα ή υπάλληλοι

  // Χρησιμοποίησε τις αμοιβές από το API αντί για τοπικό υπολογισμό
  const salaries = salariesFromAPI;

  const totalAll = salaries.reduce((sum, s) => sum + Number(s.weeklySalary), 0).toFixed(2);
  const totalPaid = salaries.reduce((sum, s) => sum + Number(s.paidAmount || 0), 0).toFixed(2);
  const totalUnpaid = (totalAll - totalPaid).toFixed(2);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Εβδομαδιαίες Αμοιβές</Typography>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => setSelectedWeek(prev => dayjs(prev).subtract(1, 'week'))}
        >
          ‹
        </Button>
        <Typography variant="h6" sx={{ minWidth: 300, textAlign: 'center' }}>
          {dayjs(weekDates[0]).format('DD/MM/YYYY')} - {dayjs(weekDates[6]).format('DD/MM/YYYY')}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setSelectedWeek(prev => dayjs(prev).add(1, 'week'))}
        >
          ›
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Υπάλληλος</TableCell>
              <TableCell>Πόστο</TableCell>
              <TableCell>Συνολικές Ώρες</TableCell>
              <TableCell>Αμοιβή Εβδομάδας (€)</TableCell>
              <TableCell align="center">Πληρωμένος</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {salaries.map(s => {
              // Get color and background based on payment status from API
              let bgColor, color, borderColor;
              if (s.paymentStatus === 'paid') {
                bgColor = '#e8f5e9';
                color = 'green';
                borderColor = 'green';
              } else if (s.paymentStatus === 'partial') {
                bgColor = '#fff3e0';
                color = 'orange';
                borderColor = 'orange';
              } else {
                bgColor = '#ffebee';
                color = 'red';
                borderColor = 'red';
              }
              
              return (
                <TableRow key={s.id}>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.position}</TableCell>
                  <TableCell>{s.totalHours}</TableCell>
                  <TableCell>
                    €{s.weeklySalary}
                    {s.paidAmount > 0 && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        Πληρωμένα: €{s.paidAmount}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: bgColor,
                        color: color,
                        borderColor: borderColor,
                        minWidth: 36,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        boxShadow: 'none',
                        border: `1.5px solid ${borderColor}`,
                        px: 1,
                        py: 0.5,
                        cursor: 'default',
                        '&:hover': {
                          backgroundColor: bgColor,
                          borderColor: borderColor,
                        }
                      }}
                      title={
                        s.paymentStatus === 'paid' 
                          ? 'Πλήρως Πληρωμένος' 
                          : s.paymentStatus === 'partial'
                          ? 'Μερικώς Πληρωμένος'
                          : 'Απλήρωτος'
                      }
                    >
                      ●
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Typography variant="h6">
          Σύνολο αμοιβών: <span style={{ color: '#1976d2' }}>€{totalAll}</span>
        </Typography>
        <Typography variant="h6">
          Πληρωμένα: <span style={{ color: 'green' }}>€{totalPaid}</span>
        </Typography>
        <Typography variant="h6">
          Υπόλοιπο: <span style={{ color: 'red' }}>€{totalUnpaid}</span>
        </Typography>
      </Box>
    </Box>
  );
}
